/** @jsx jsx */
/** @jsxFrag null */
import {jsx, ParamComponent} from 'panel';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import {VNode} from 'snabbdom';
import type {JsxVNodeProps} from 'snabbdom-jsx-lite';

import {PanelContextToReactProvider} from 'common/contexts/react/providers';
import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {getReactComponentId, Providers} from './react';

interface Params {
  props: any;
}

interface JsxProps {
  params: Params;
}

interface Options<Contexts extends Array<keyof IronContextRegistry>> {
  /** Contexts to subscribe to. */
  contexts?: Contexts;
  defaultContexts?: () => Partial<Record<Contexts[number], any>>;
  /** Optional styles to add to the wrapper "content" div */
  wrapperStyles?: Record<string, string>;
  /**
   * Use the shadow dom.
   *
   * WARNING: Do not disable this unless you have removed host styles from your component. It might affect other components!
   */
  useShadowDom?: boolean;
}

interface PanelProps {
  styleOverride?: string;
}

/**
 * Wraps a React component with a Panel component.
 */
export function react2panel<
  Component extends React.JSXElementConstructor<any>,
  Contexts extends Array<keyof IronContextRegistry>,
>(
  name: string,
  reactComponent: Component,
  opts?: Options<Contexts>,
): (
  props: Pick<JsxVNodeProps, 'sel' | 'hook'> &
    PanelProps &
    Omit<React.ComponentPropsWithRef<Component>, Contexts[number]>,
) => VNode {
  const componentId = (reactComponent as any).componentId || getReactComponentId();

  class ReactRoot extends ParamComponent<Params, unknown, IronContextRegistry> {
    private rootEl: HTMLElement;
    private reactRoot: ReactDOM.Root;

    get config() {
      return {
        defaultContexts: opts?.defaultContexts ? opts.defaultContexts() : {},
        params: {
          props: Object,
        },
        ...(opts?.contexts ? {contexts: opts.contexts} : {}),
        useShadowDom: opts?.useShadowDom,
        template: () => (
          <div
            style={{
              // make sure the root isn't causing any styling changes
              display: `contents`,
              ...(opts?.wrapperStyles ? opts.wrapperStyles : {}),
            }}
            hook={{
              insert: (vnode) => {
                this.rootEl = vnode.elm as HTMLElement;
              },
            }}
          />
        ),
        hooks: {
          postUpdate: () => this.render(),
        },
      };
    }

    /**
     * Renders our React component.
     */
    render() {
      if (!this.isConnected) {
        return;
      }

      const props = this.params.props;
      const contextValues = Object.fromEntries(
        // create a new Proxy on each render so that the consumed value always triggers a prop update when passed into a PureComponent
        opts?.contexts?.map((context) => [context, new Proxy(this.getContext(context), {})]) || [],
      );

      // add contexts to props
      let element: React.ReactNode = React.createElement(reactComponent, {
        ...props,
        ...contextValues,
      });

      // wrap component in providers
      const providers = Object.entries(contextValues)
        .map(([context, value]) => {
          const Provider = PanelContextToReactProvider[context];
          return Provider ? React.createElement(Provider, {value}) : null;
        })
        .filter(Boolean);

      if (providers.length) {
        element = React.createElement(Providers, {providers, children: element});
      }

      this.reactRoot.render(element);
    }

    connectedCallback(): void {
      super.connectedCallback();

      // set up the react root
      this.reactRoot = ReactDOM.createRoot(this.rootEl);
      this.render();
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      this.reactRoot.unmount();
    }
  }

  const panelTagName = `${name}-${componentId}`;
  const ReactRootEl = defineAndMakeJsxFactory<JsxProps>(panelTagName, ReactRoot);

  const Component = (props) => {
    const {sel, hook, ...rest} = props ?? {};

    return (
      <ReactRootEl
        sel={sel}
        hook={hook}
        params={{
          props: rest,
        }}
      />
    );
  };

  /** Accesses the Panel wrapper's tag name. ONLY rely on this for legacy use cases. */
  Component.panelTagName = panelTagName;

  return Component;
}
